import { ENTITY_TYPE } from '@/components/model/nlu-model/sidebar/entities/entity-types'

/**
 * Simple tree for combining response from ML entities in inspect(test) tab.
 * We're getting list of entities without specifying if it's child or not
 */
export class MachineLearningTestPreviewService {
  constructor () {
    // List of all ids in ML entity(including it's children)
    this.mlEntityIds = {}
    this.children = []
    this.allMlEntities = []
  }

  updatePossibleMlEntities (allMlEntities) {
    this.mlEntityIds = []
    this.allMlEntities = allMlEntities
    this._updateMlEntityIds()
  }

  /**
     * Children don't allow to have some fields due to BE validation
     * @param mlEntities - returned PreviewTree from generatePreviewTree
     */
  static removeForbiddenParams (mlEntities) {
    return mlEntities.map(mlEntity => ({
      ...this._cleanUpNode(mlEntity),
      // we need to remove value only in child entities
      value: mlEntity.value
    }))
  }

  static _cleanUpNode ({ id, role, type, value, ...rest }) {
    if (rest.children) {
      return {
        ...rest,
        children: rest.children.map(child => this._cleanUpNode(child))
      }
    }
    return rest
  }

  /**
   * @param predictedEntities
   */
  generatePreviewTree (predictedEntities) {
    this.children = []
    this._createMlTree(predictedEntities)
    return this.children
  }

  _updateMlEntityIds () {
    this.allMlEntities.forEach(entity => {
      this.mlEntityIds[entity.id] = []
      const stack = [...(entity.children || [])]
      while (stack.length !== 0) {
        const child = stack.pop()
        this.mlEntityIds[entity.id].push(child.id)
        if (child.children.length !== 0) {
          stack.push(...child.children)
        }
      }
    })
  }

  _createMlTree (predictedEntities) {
    const childEntities = []
    // Setting up Head nodes (they're kind of unique ones = can't overlap if they have same name)
    predictedEntities.forEach(entity => {
      if (this.mlEntityIds[entity.id]) {
        this.children.push({
          ...entity,
          children: []
        })
      } else {
        childEntities.push(entity)
      }
    })

    childEntities.forEach(entity => {
      try {
        const headNode = this._getHeadNode(entity)
        this._addNode(headNode.children, entity)
      } catch (e) {
        console.error(e.toString())
      }
    })
  }

  _getHeadNode (entity) {
    const headNode = this.children.find(child => {
      if (child.startPos > entity.startPos || child.endPos < entity.endPos) {
        return false
      }
      return !!this.mlEntityIds[child.id].find(id => id === entity.id)
    })

    if (!headNode) {
      const error = new Error(`Was not able to find headNode for ${entity.name}`)
      error.customFields = { entity }
      throw error
    }

    return headNode
  }

  _addNode (children, node) {
    let hasUpdated = false
    for (let i = 0; i < children.length; i++) {
      const child = children[i]
      //  {} - child, node - [] => { [ ] }
      if ((child.endPos >= node.endPos && child.startPos < node.startPos) || (child.endPos > node.endPos && child.startPos <= node.startPos)) {
        if (!child.children) {
          child.children = [node]
        } else {
          this._addNode(child.children, node)
        }
        hasUpdated = true
        //  {} - child, node - [] => [ {} ]
      } else if ((child.endPos <= node.endPos && child.startPos > node.startPos) || (child.endPos < node.endPos && child.startPos >= node.startPos)) {
        children[i] = node
        if (!node.children) {
          node.children = [child]
        } else {
          this._addNode(node.children, child)
        }
        hasUpdated = true
        //  {} - child, node - [] => [{]} (don't know who's a child|parent)
      } else if (child.endPos === node.endPos && child.startPos === node.startPos) {
        if (this._isChild(child, node)) {
          if (!child.children) {
            child.children = [node]
          } else {
            this._addNode(child.children, node)
          }
        } else {
          children[i] = node
          if (!node.children) {
            node.children = [child]
          } else {
            this._addNode(node.children, child)
          }
        }
        hasUpdated = true
      }
    }

    if (!hasUpdated) {
      children.push(node)
    }
  }

  _isChild (node1, node2) {
    const headNodeId = this._getHeadNode(node1).id
    const headNode = this.allMlEntities.find(entity => entity.id === headNodeId)

    const queue = [headNode]
    while (queue.length !== 0) {
      const child = queue.shift()
      if (child.id === node1.id) {
        return true
      }
      if (child.id === node2.id) {
        return false
      }
      queue.push(...(child.children || []))
    }
    console.error('Not in the same tree:', node1, node2)
  }

  static cleanUpTestResponseEntities (allEntities, predictedEntities) {
    const predictedMlEntities = []
    const predictedNotMlEntities = []

    predictedEntities.forEach(predictedEntity => {
      const entity = allEntities.find(e => e.name === predictedEntity.entity)
      // We're assuming that BE can return only mapped entity.
      // In that case if entity does not exists = it's child of some ML entity
      if (!entity || entity.type === ENTITY_TYPE.machineLearning) {
        predictedMlEntities.push({
          ...predictedEntity,
          type: ENTITY_TYPE.machineLearning,
          children: []
        })
      } else {
        predictedNotMlEntities.push({
          ...predictedEntity,
          type: entity.type
        })
      }
    })

    return {
      predictedMlEntities,
      predictedNotMlEntities
    }
  }
}
